/**
 * HR edit dialogs for one employee record. Each dialog sends only the fields
 * that were changed, so a partial form never blanks data it did not show.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api, ApiError, get, patch, post, put, upload } from '../lib/api.ts';
import { DOCUMENT_HINT, DOCUMENT_TYPES, documentProblem, PERSON_DOCUMENTS, type PersonDocumentKind } from '../lib/documents.ts';
import { date, todayIn } from '../lib/format.ts';
import { useLookups } from '../lib/session.ts';
import { Button, Callout, Dialog, Field, Switch, useToast } from './ui.tsx';

export type EditKind = 'personal' | 'contact' | 'identity' | 'bank' | 'family' | 'emergency' | 'contract' | 'salary' | 'employment';

const err = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong.');

function useSave(id: string, onDone: () => void, fn: () => Promise<unknown>, done = 'Saved') {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['person', id] });
      void qc.invalidateQueries({ queryKey: ['my-change-requests'] });
      toast({ title: done });
      onDone();
    },
  });
}

const SENT = 'Sent to Human Capital';

function Shell({ title, eyebrow, onClose, onSave, pending, error, children, submit = 'Save', width }: { title: ReactNode; eyebrow: string; onClose: () => void; onSave: () => void; pending: boolean; error: unknown; children: ReactNode; submit?: string; width?: number }) {
  return (
    <Dialog open onClose={onClose} eyebrow={eyebrow} title={title} width={width} footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" loading={pending} onClick={onSave}>{submit}</Button></>}>
      <div className="grid-12" style={{ gap: 14 }}>
        {children}
        {error ? <div className="col-12"><Callout tone="bad">{err(error)}</Callout></div> : null}
      </div>
    </Dialog>
  );
}

/** Tracks which fields were touched and returns only those. */
function useForm<T extends Record<string, unknown>>(initial: T) {
  const [values, setValues] = useState(initial);
  const [touched, setTouched] = useState<Set<keyof T>>(new Set());
  const set = <K extends keyof T>(k: K, v: T[K]) => {
    setValues((x) => ({ ...x, [k]: v }));
    setTouched((t) => new Set(t).add(k));
  };
  const changed = () => Object.fromEntries([...touched].map((k) => [k, values[k] === '' ? null : values[k]])) as Partial<T>;
  return { values, set, changed, dirty: touched.size > 0 };
}

export interface PersonSnapshot {
  givenName: string;
  familyName: string | null;
  preferredName?: string | null;
  gender: string | null;
  birthDate: string | null;
  birthPlace: string | null;
  religion?: Ref | null;
  maritalStatus?: Ref | null;
  educationLevel?: Ref | null;
  childrenCount?: number | null;
  spouseName?: string | null;
  university?: string | null;
  highSchool?: string | null;
  training?: string | null;
  uniformSize?: string | null;
  contact: null | { workEmail: string | null; personalEmail: string | null; mobilePhone: string | null; currentAddress: string | null; currentCity: string | null; altMobilePhone?: string | null; residentAddress?: string | null; residentCity?: string | null };
  identity: null | { ptkpCode: string };
  account?: { manageable: boolean } | null;
  office?: { id: string } | null;
  employment: null | {
    type: string;
    contractEndsOn: string | null;
    department?: Ref | null;
    division?: Ref | null;
    section?: Ref | null;
    jobPosition?: Ref | null;
    jobLevel?: Ref | null;
    attendanceExempt?: boolean;
    holidayAllowanceEligible?: boolean;
    mandatorySaving?: boolean;
  };
}
type Ref = { id?: string; name: string };

/** `request`: the person asks HR for the change (Approvals → Profile changes) instead of saving it. */
type Props = { id: string; p: PersonSnapshot; onClose: () => void; request?: boolean; contract?: ContractRef };

function text(label: string, value: string, onChange: (v: string) => void, cols = 6, opts: { mono?: boolean; type?: string; hint?: string; disabled?: boolean; inputMode?: 'numeric' | 'decimal' | 'email' | 'tel' } = {}) {
  return (
    <div className={`col-${cols}`}>
      <Field label={label} hint={opts.hint}>{(f) => <input {...f} className={`input${opts.mono ? ' input--mono' : ''}`} type={opts.type ?? 'text'} inputMode={opts.inputMode} disabled={opts.disabled} value={value} onChange={(e) => onChange(e.target.value)} />}</Field>
    </div>
  );
}

export function PersonEditDialog({ kind, ...props }: Props & { kind: EditKind }) {
  const C = { personal: PersonalDialog, contact: ContactDialog, identity: IdentityDialog, bank: BankDialog, family: FamilyDialog, emergency: EmergencyDialog, contract: ContractDialog, salary: SalaryDialog, employment: EmploymentDialog }[kind];
  return <C {...props} />;
}

function PersonalDialog({ id, p, onClose, request }: Props) {
  const lookups = useLookups();
  const f = useForm({
    givenName: p.givenName,
    familyName: p.familyName ?? '',
    preferredName: p.preferredName ?? '',
    gender: p.gender ?? '',
    birthDate: p.birthDate ?? '',
    birthPlace: p.birthPlace ?? '',
    religionId: p.religion?.id ?? '',
    maritalStatusId: p.maritalStatus?.id ?? '',
    educationLevelId: p.educationLevel?.id ?? '',
    spouseName: p.spouseName ?? '',
    childrenCount: p.childrenCount == null ? '' : String(p.childrenCount),
    university: p.university ?? '',
    highSchool: p.highSchool ?? '',
    training: p.training ?? '',
    uniformSize: p.uniformSize ?? '',
  });
  const [reason, setReason] = useState('');
  const changed = () => {
    const c: Record<string, unknown> = f.changed();
    if ('childrenCount' in c) c.childrenCount = c.childrenCount === null ? null : Number(c.childrenCount);
    return c;
  };
  const m = useSave(id, onClose, () => (request ? post('/api/people/requests/profile', { section: 'personal', changes: changed(), reason: reason || undefined }) : patch(`/api/people/${id}/personal`, changed())), request ? SENT : undefined);
  const pick = (label: string, k: 'religionId' | 'maritalStatusId' | 'educationLevelId', list: { id: string; name: string }[] | undefined, hint?: string) => (
    <div className="col-4"><Field label={label} hint={hint}>{(a) => <select {...a} className="select" value={f.values[k]} onChange={(e) => f.set(k, e.target.value)}><option value="">—</option>{list?.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>}</Field></div>
  );
  return (
    <Shell eyebrow="Personal" title={request ? 'Ask HR to change your details' : 'Edit personal details'} submit={request ? 'Send to HR' : undefined} onClose={onClose} onSave={() => (f.dirty ? m.mutate() : onClose())} pending={m.isPending} error={m.error} width={680}>
      {text('Given name', f.values.givenName, (v) => f.set('givenName', v))}
      {text('Family name', f.values.familyName, (v) => f.set('familyName', v))}
      {text('Called', f.values.preferredName, (v) => f.set('preferredName', v), 6, { hint: 'Shown on kiosks and greetings' })}
      <div className="col-6"><Field label="Gender">{(a) => <select {...a} className="select" value={f.values.gender} onChange={(e) => f.set('gender', e.target.value)}><option value="">—</option><option value="female">Female</option><option value="male">Male</option></select>}</Field></div>
      {text('Born on', f.values.birthDate, (v) => f.set('birthDate', v), 6, { type: 'date' })}
      {text('Born in', f.values.birthPlace, (v) => f.set('birthPlace', v))}
      {pick('Religion', 'religionId', lookups.data?.religions, 'Decides THR feast')}
      {pick('Marital status', 'maritalStatusId', lookups.data?.maritalStatuses)}
      {pick('Education', 'educationLevelId', lookups.data?.educationLevels)}
      {text('Spouse', f.values.spouseName, (v) => f.set('spouseName', v), 8)}
      {text('Children', f.values.childrenCount, (v) => f.set('childrenCount', v.replace(/\D/g, '').slice(0, 2)), 4, { inputMode: 'numeric' })}
      {text('University', f.values.university, (v) => f.set('university', v))}
      {text('High school', f.values.highSchool, (v) => f.set('highSchool', v))}
      {text('Training', f.values.training, (v) => f.set('training', v), 8)}
      {text('Uniform size', f.values.uniformSize, (v) => f.set('uniformSize', v), 4)}
      {request ? text('Why', reason, setReason, 12, { hint: 'Optional, e.g. “married in September”' }) : null}
    </Shell>
  );
}

function ContactDialog({ id, p, onClose, request }: Props) {
  const c = p.contact;
  const f = useForm({ workEmail: c?.workEmail ?? '', personalEmail: c?.personalEmail ?? '', mobilePhone: c?.mobilePhone ?? '', altMobilePhone: c?.altMobilePhone ?? '', currentAddress: c?.currentAddress ?? '', currentCity: c?.currentCity ?? '', residentAddress: c?.residentAddress ?? '', residentCity: c?.residentCity ?? '' });
  const [reason, setReason] = useState('');
  // The API refuses HR a private e-mail change on an account with access they lack (password links go there).
  const locked = !request && p.account?.manageable === false;
  const m = useSave(id, onClose, () => (request ? post('/api/people/requests/profile', { section: 'contact', changes: f.changed(), reason: reason || undefined }) : patch(`/api/people/${id}/contact`, f.changed())), request ? SENT : undefined);
  return (
    <Shell eyebrow="Contact" title={request ? 'Ask HR to change your contact details' : 'Edit contact details'} submit={request ? 'Send to HR' : undefined} onClose={onClose} onSave={() => (f.dirty ? m.mutate() : onClose())} pending={m.isPending} error={m.error}>
      {text('Work e-mail', f.values.workEmail, (v) => f.set('workEmail', v), 6, { inputMode: 'email' })}
      {text('Personal e-mail', f.values.personalEmail, (v) => f.set('personalEmail', v), 6, { inputMode: 'email', ...(locked ? { disabled: true, hint: 'They have access you don’t, and password links can go here. They ask for this change from their own profile.' } : {}) })}
      {text('Mobile', f.values.mobilePhone, (v) => f.set('mobilePhone', v), 6, { mono: true, inputMode: 'tel' })}
      {text('Other mobile', f.values.altMobilePhone, (v) => f.set('altMobilePhone', v), 6, { mono: true, inputMode: 'tel' })}
      {text('Lives at', f.values.currentAddress, (v) => f.set('currentAddress', v), 8)}
      {text('City', f.values.currentCity, (v) => f.set('currentCity', v), 4)}
      {text('Address on the KTP', f.values.residentAddress, (v) => f.set('residentAddress', v), 8)}
      {text('City', f.values.residentCity, (v) => f.set('residentCity', v), 4)}
      {request ? text('Why', reason, setReason, 12, { hint: 'Optional' }) : null}
    </Shell>
  );
}

function IdentityDialog({ id, p, onClose }: Props) {
  const f = useForm({ nationalId: '', familyCard: '', npwp: '', bpjsEmployment: '', bpjsHealth: '', ptkpCode: p.identity?.ptkpCode ?? 'TK/0' });
  const m = useSave(id, onClose, () => put(`/api/people/${id}/identity`, f.changed()));
  return (
    <Shell eyebrow="Restricted" title="Update identity numbers" onClose={onClose} onSave={() => (f.dirty ? m.mutate() : onClose())} pending={m.isPending} error={m.error}>
      <div className="col-12"><Callout tone="info">Fill only what changes. Numbers are encrypted when saved and never written to the audit trail.</Callout></div>
      {text('NIK', f.values.nationalId, (v) => f.set('nationalId', v.replace(/\D/g, '')), 6, { mono: true, inputMode: 'numeric', hint: '16 digits' })}
      {text('Family card (KK)', f.values.familyCard, (v) => f.set('familyCard', v.replace(/\D/g, '')), 6, { mono: true, inputMode: 'numeric' })}
      {text('NPWP', f.values.npwp, (v) => f.set('npwp', v), 6, { mono: true })}
      <div className="col-6"><Field label="Tax status (PTKP)">{(a) => <select {...a} className="select" value={f.values.ptkpCode} onChange={(e) => f.set('ptkpCode', e.target.value)}>{['TK/0', 'TK/1', 'TK/2', 'TK/3', 'K/0', 'K/1', 'K/2', 'K/3'].map((c) => <option key={c}>{c}</option>)}</select>}</Field></div>
      {text('BPJS Ketenagakerjaan', f.values.bpjsEmployment, (v) => f.set('bpjsEmployment', v), 6, { mono: true })}
      {text('BPJS Kesehatan', f.values.bpjsHealth, (v) => f.set('bpjsHealth', v), 6, { mono: true })}
    </Shell>
  );
}

function BankDialog({ id, p, onClose }: Props) {
  const lookups = useLookups();
  const qc = useQueryClient();
  const [f, setF] = useState({ bankId: '', accountNo: '', holderName: `${p.givenName} ${p.familyName ?? ''}`.trim(), branch: '', isOwnAccount: true, reason: '' });
  const [proof, setProof] = useState<File | null>(null);
  // Set once the account is saved, so a failed upload retries the upload alone.
  const [saved, setSaved] = useState(false);
  const problem = documentProblem(proof);
  const m = useSave(id, onClose, async () => {
    if (!saved) {
      await put(`/api/people/${id}/bank`, { ...f, bankId: f.bankId || lookups.data?.banks[0]?.id, branch: f.branch || null });
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['person', id] });
    }
    if (proof) await upload(`/api/people/${id}/documents/bank_book`, proof);
  });
  return (
    <Shell eyebrow="Restricted" title="Change salary account" onClose={onClose} onSave={() => (problem ? undefined : m.mutate())} pending={m.isPending} error={m.error} submit={saved ? 'Upload' : 'Change account'}>
      {saved ? <div className="col-12"><Callout tone="info">The account is saved. Only the bank book is left to upload.</Callout></div> : null}
      <div className="col-12"><Callout tone="warn" title="The employee is told by e-mail">Salary redirection is the most common payroll fraud. Confirm the change with the employee in person, and keep the proof.</Callout></div>
      <div className="col-6"><Field label="Bank">{(a) => <select {...a} className="select" value={f.bankId} onChange={(e) => setF({ ...f, bankId: e.target.value })}>{lookups.data?.banks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</select>}</Field></div>
      {text('Account number', f.accountNo, (v) => setF({ ...f, accountNo: v }), 6, { mono: true, inputMode: 'numeric', hint: 'BCA 10 digits · Mandiri 13' })}
      {text('Account holder', f.holderName, (v) => setF({ ...f, holderName: v }))}
      {text('Branch', f.branch, (v) => setF({ ...f, branch: v }))}
      <div className="col-12"><Switch checked={f.isOwnAccount} onChange={(v) => setF({ ...f, isOwnAccount: v })} label="The account is in the employee’s own name" showLabel /></div>
      {text('Why', f.reason, (v) => setF({ ...f, reason: v }), 12, { hint: 'Kept in the audit trail, e.g. “new account, form signed 3 Nov”' })}
      <DocumentField label="Bank book (optional)" onChange={setProof} problem={problem} />
    </Shell>
  );
}

function FamilyDialog({ id, p, onClose }: Props) {
  const [f, setF] = useState({ relation: 'child', name: '', phone: '', city: '', deceased: false });
  const m = useSave(id, onClose, () => post(`/api/people/${id}/family`, { ...f, phone: f.phone || null, city: f.city || null }));
  return (
    <Shell eyebrow="Family" title="Add a family member" onClose={onClose} onSave={() => m.mutate()} pending={m.isPending} error={m.error} submit="Add">
      <div className="col-6"><Field label="Relation">{(a) => <select {...a} className="select" value={f.relation} onChange={(e) => setF({ ...f, relation: e.target.value })}>{['spouse', 'child', 'father', 'mother', 'sibling', 'other'].map((r) => <option key={r} value={r}>{r}</option>)}</select>}</Field></div>
      {text('Name', f.name, (v) => setF({ ...f, name: v }))}
      {text('Phone', f.phone, (v) => setF({ ...f, phone: v }), 6, { mono: true, inputMode: 'tel' })}
      {text('City', f.city, (v) => setF({ ...f, city: v }))}
      <div className="col-12"><Switch checked={f.deceased} onChange={(v) => setF({ ...f, deceased: v })} label="Deceased" showLabel /></div>
    </Shell>
  );
}

function EmergencyDialog({ id, p, onClose }: Props) {
  const [f, setF] = useState({ name: '', relationship: '', phone: '', city: '' });
  const m = useSave(id, onClose, () => post(`/api/people/${id}/emergency`, { ...f, city: f.city || null }));
  return (
    <Shell eyebrow="Emergency" title="Add an emergency contact" onClose={onClose} onSave={() => m.mutate()} pending={m.isPending} error={m.error} submit="Add">
      {text('Name', f.name, (v) => setF({ ...f, name: v }))}
      {text('Relationship', f.relationship, (v) => setF({ ...f, relationship: v }))}
      {text('Phone', f.phone, (v) => setF({ ...f, phone: v }), 6, { mono: true, inputMode: 'tel' })}
      {text('City', f.city, (v) => setF({ ...f, city: v }))}
    </Shell>
  );
}

/** The signed contract. */
function DocumentField({ label, onChange, problem }: { label: string; onChange: (f: File | null) => void; problem: string | null }) {
  return (
    <div className="col-12">
      <Field label={label} hint={DOCUMENT_HINT} error={problem}>
        {(a) => <input {...a} className="input" type="file" accept={DOCUMENT_TYPES} onChange={(e) => onChange(e.target.files?.[0] ?? null)} />}
      </Field>
    </div>
  );
}

export interface ContractRef { id: string; type: string; startsOn: string; endsOn: string | null; hasDocument: boolean }

/** A new contract or extension, with its signed copy if there is one; with `contract`, uploads or replaces that contract's document. */
function ContractDialog(props: Props) {
  return props.contract ? <ContractDocumentDialog {...props} contract={props.contract} /> : <NewContractDialog {...props} />;
}

function NewContractDialog({ id, p, onClose }: Props) {
  const today = todayIn();
  const qc = useQueryClient();
  const [f, setF] = useState({ type: p.employment?.type === 'permanent' ? 'permanent' : 'contract', startsOn: p.employment?.contractEndsOn ?? today, endsOn: '', extension: !!p.employment?.contractEndsOn, secondCompany: false });
  const [file, setFile] = useState<File | null>(null);
  // Set once the contract is saved, so a failed upload retries the upload alone.
  const [saved, setSaved] = useState<string | null>(null);
  const problem = documentProblem(file, false);
  const m = useSave(id, onClose, async () => {
    let cid = saved;
    if (!cid) {
      cid = (await post<{ id: string }>(`/api/people/${id}/contracts`, { ...f, endsOn: f.type === 'permanent' ? null : f.endsOn || null })).id;
      setSaved(cid);
      void qc.invalidateQueries({ queryKey: ['person', id] });
    }
    if (file) await upload(`/api/people/${id}/contracts/${cid}/document`, file);
  });
  return (
    <Shell eyebrow="Contract" title={f.extension ? 'Extend the contract' : 'New contract'} submit={saved ? 'Upload' : undefined} onClose={onClose} onSave={() => (problem ? undefined : m.mutate())} pending={m.isPending} error={m.error}>
      {saved ? <div className="col-12"><Callout tone="info">The contract is saved. Only its document is left to upload.</Callout></div> : null}
      <div className="col-12"><Field label="Type">{(a) => <select {...a} className="select" disabled={!!saved} value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>{['contract', 'probation', 'permanent', 'internship', 'freelance'].map((x) => <option key={x} value={x}>{x}</option>)}</select>}</Field></div>
      {saved ? null : (
        <>
          {text('Starts', f.startsOn, (v) => setF({ ...f, startsOn: v }), 6, { type: 'date', hint: f.startsOn > today ? 'Dated ahead: it takes effect on this day' : undefined })}
          {f.type === 'permanent' ? null : text('Ends', f.endsOn, (v) => setF({ ...f, endsOn: v }), 6, { type: 'date' })}
          <div className="col-12"><Switch checked={f.extension} onChange={(v) => setF({ ...f, extension: v })} label="Extension of the current contract" showLabel /></div>
          <div className="col-12"><Switch checked={f.secondCompany} onChange={(v) => setF({ ...f, secondCompany: v })} label="With the second company (split pay)" showLabel /></div>
        </>
      )}
      <DocumentField label="Signed contract (optional)" onChange={setFile} problem={problem} />
    </Shell>
  );
}

function ContractDocumentDialog({ id, contract, onClose }: Props & { contract: ContractRef }) {
  const [file, setFile] = useState<File | null>(null);
  const [tried, setTried] = useState(false);
  const problem = documentProblem(file, tried);
  const m = useSave(id, onClose, () => upload(`/api/people/${id}/contracts/${contract.id}/document`, file!), contract.hasDocument ? 'Document replaced' : 'Document uploaded');
  const save = () => {
    setTried(true);
    if (!documentProblem(file, true)) m.mutate();
  };
  return (
    <Shell eyebrow="Contract" title={contract.hasDocument ? 'Replace the signed contract' : 'Upload the signed contract'} submit={contract.hasDocument ? 'Replace' : 'Upload'} onClose={onClose} onSave={save} pending={m.isPending} error={m.error}>
      <div className="col-12">
        <p className="muted"><span style={{ textTransform: 'capitalize' }}>{contract.type}</span> · {date(contract.startsOn)} → {contract.endsOn ? date(contract.endsOn) : 'open-ended'}</p>
      </div>
      {contract.hasDocument ? <div className="col-12"><Callout tone="info">The employee and HR will see the new file. The one it replaces is kept, and the audit trail names it.</Callout></div> : null}
      <DocumentField label="Signed contract" onChange={setFile} problem={problem} />
    </Shell>
  );
}

interface Structure {
  units: { id: string; kind: 'department' | 'division' | 'section'; name: string }[];
  links: { parentKind: string; parentId: string; childId: string }[];
  /** Positions tied to each unit. */
  positions: { unitId: string; positionId: string }[];
}
export type Placement = { departmentId: string; divisionId: string; sectionId: string; jobPositionId: string };
type Option = { id: string; name: string };

export const useStructure = () => useQuery({ queryKey: ['org-structure'], queryFn: () => get<Structure>('/api/org/structure'), staleTime: 5 * 60_000 }).data;

/** Units of `kind` linked under the first parent that has any; every unit of that kind when none do (`strict`: none). The current value always stays listed. */
function unitOptions(s: Structure | undefined, kind: Structure['units'][number]['kind'], parents: { kind: string; id: string | undefined }[], current: string, strict = false) {
  if (!s) return [];
  const all = s.units.filter((u) => u.kind === kind);
  const keep = (list: Option[]) => (list.some((u) => u.id === current) || !current ? list : [...list, ...all.filter((u) => u.id === current)]);
  for (const parent of parents) {
    if (!parent.id) continue;
    const ids = new Set(s.links.filter((l) => l.parentKind === parent.kind && l.parentId === parent.id).map((l) => l.childId));
    const linked = all.filter((u) => ids.has(u.id));
    if (linked.length || strict) return keep(linked);
  }
  return strict ? keep([]) : all;
}

/** The positions offered for a placement: those tied to the section, else the division, else the department; every position when none of them has any tied. */
export function positionOptions(s: Structure | undefined, all: Option[], v: Placement) {
  const tied = (unitId: string) => s?.positions.filter((p) => p.unitId === unitId).map((p) => p.positionId) ?? [];
  const unitId = [v.sectionId, v.divisionId, v.departmentId].find((u) => u && tied(u).length);
  const chosen = [v.sectionId, v.divisionId, v.departmentId].find(Boolean);
  const unit = s?.units.find((u) => u.id === (unitId ?? chosen)) ?? null;
  if (!unitId) return { options: all, tiedTo: null, untied: unit };
  const ids = new Set(tied(unitId));
  return { options: all.filter((p) => ids.has(p.id)), tiedTo: unit, untied: null };
}

/**
 * Department → division → section → position, each narrowed to the level above: a section only under its division,
 * a position only among those tied to the section or division. Changing a level clears what no longer fits below it.
 */
export function PlacementFields({ officeId, value, onChange, positions, currentPosition, cols = 4, errors = {} }: { officeId: string | undefined; value: Placement; onChange: (patch: Partial<Placement>) => void; positions: Option[]; currentPosition?: Ref | null; cols?: number; errors?: Partial<Record<keyof Placement, string | null>> }) {
  const s = useStructure();
  // An archived position stays listed while it is the current one.
  const all = currentPosition?.id && !positions.some((x) => x.id === currentPosition.id) ? [...positions, { id: currentPosition.id, name: currentPosition.name }] : positions;
  const change = (patch: Partial<Placement>) => {
    const next = { ...value, ...patch };
    const fits = !next.jobPositionId || positionOptions(s, all, next).options.some((o) => o.id === next.jobPositionId);
    onChange(fits ? patch : { ...patch, jobPositionId: '' });
  };
  const pick = (label: string, key: keyof Placement, options: Option[], patch: (v: string) => Partial<Placement>, opts: { empty?: string; hint?: string | null; width?: number } = {}) => (
    <div className={`col-${opts.width ?? cols}`}>
      <Field label={label} hint={opts.hint ?? undefined} error={errors[key] ?? null}>
        {(a) => (
          <select {...a} className="select" value={value[key]} disabled={!options.length && !value[key]} onChange={(e) => change(patch(e.target.value))}>
            <option value="">{options.length || value[key] ? '—' : (opts.empty ?? '—')}</option>
            {options.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}
      </Field>
    </div>
  );
  const sections = value.divisionId ? unitOptions(s, 'section', [{ kind: 'division', id: value.divisionId }], value.sectionId, true) : [];
  const pos = positionOptions(s, all, value);
  return (
    <>
      {pick('Department', 'departmentId', unitOptions(s, 'department', [{ kind: 'office', id: officeId }], value.departmentId), (v) => ({ departmentId: v, divisionId: '', sectionId: '' }))}
      {pick('Division', 'divisionId', unitOptions(s, 'division', [{ kind: 'department', id: value.departmentId }, { kind: 'office', id: officeId }], value.divisionId), (v) => ({ divisionId: v, sectionId: '' }))}
      {pick('Section', 'sectionId', sections, (v) => ({ sectionId: v }), { empty: value.divisionId ? 'No sections in this division' : 'Choose a division first' })}
      {pick('Position', 'jobPositionId', pos.options, (v) => ({ jobPositionId: v }), {
        width: 6,
        hint: pos.tiedTo ? `Positions of the ${pos.tiedTo.kind} ${pos.tiedTo.name}` : pos.untied ? `No positions are tied to ${pos.untied.name} yet, so all are offered. Tie them under Organisation.` : null,
      })}
    </>
  );
}

/** Moves between units, positions and levels, and the employment flags payroll and attendance read. */
function EmploymentDialog({ id, p, onClose }: Props) {
  const lookups = useLookups();
  const qc = useQueryClient();
  const e = p.employment;
  const f = useForm({
    departmentId: e?.department?.id ?? '',
    divisionId: e?.division?.id ?? '',
    sectionId: e?.section?.id ?? '',
    jobPositionId: e?.jobPosition?.id ?? '',
    jobLevelId: e?.jobLevel?.id ?? '',
    attendanceExempt: e?.attendanceExempt ?? false,
    holidayAllowanceEligible: e?.holidayAllowanceEligible ?? false,
    mandatorySaving: e?.mandatorySaving ?? false,
  });
  const m = useSave(id, onClose, async () => {
    await patch(`/api/people/${id}/employment`, f.changed());
    void qc.invalidateQueries({ queryKey: ['people'] });
  });
  // An archived position or level stays visible while it is the current one.
  const withCurrent = <T extends { id: string; name: string }>(list: T[] | undefined, cur: Ref | null | undefined) => (cur?.id && !list?.some((x) => x.id === cur.id) ? [...(list ?? []), { id: cur.id, name: cur.name } as T] : (list ?? []));
  return (
    <Shell eyebrow="Employment" title="Edit employment" onClose={onClose} onSave={() => (f.dirty ? m.mutate() : onClose())} pending={m.isPending} error={m.error} width={680}>
      <PlacementFields officeId={p.office?.id} value={f.values} positions={lookups.data?.jobPositions ?? []} currentPosition={e?.jobPosition} onChange={(patch) => Object.entries(patch).forEach(([k, v]) => f.set(k as keyof Placement, v as string))} />
      <div className="col-6"><Field label="Level">{(a) => <select {...a} className="select" value={f.values.jobLevelId} onChange={(ev) => f.set('jobLevelId', ev.target.value)}><option value="">—</option>{withCurrent(lookups.data?.jobLevels, e?.jobLevel).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select>}</Field></div>
      <div className="col-12"><Switch checked={f.values.attendanceExempt} onChange={(v) => f.set('attendanceExempt', v)} label="Attendance exempt (never counted absent)" showLabel /></div>
      <div className="col-12"><Switch checked={f.values.holidayAllowanceEligible} onChange={(v) => f.set('holidayAllowanceEligible', v)} label="Eligible for THR" showLabel /></div>
      <div className="col-12"><Switch checked={f.values.mandatorySaving} onChange={(v) => f.set('mandatorySaving', v)} label="Mandatory cooperative saving" showLabel /></div>
    </Shell>
  );
}

function SalaryDialog({ id, onClose }: Props) {
  const [f, setF] = useState({ effectiveFrom: '', basicSalary: '', reason: '' });
  const m = useSave(id, onClose, () => post(`/api/people/${id}/salary`, { ...f, basicSalary: f.basicSalary.replace(/\D/g, '') }));
  return (
    <Shell eyebrow="Salary" title="Change basic salary" onClose={onClose} onSave={() => m.mutate()} pending={m.isPending} error={m.error}>
      {text('From', f.effectiveFrom, (v) => setF({ ...f, effectiveFrom: v }), 6, { type: 'date', hint: 'Usually the 26th, the first day of a pay period' })}
      {text('Basic salary (Rp)', f.basicSalary, (v) => setF({ ...f, basicSalary: v.replace(/[^\d]/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.') }), 6, { mono: true, inputMode: 'numeric' })}
      {text('Reason', f.reason, (v) => setF({ ...f, reason: v }), 12, { hint: 'e.g. annual review, promotion, contract renewal' })}
      <div className="col-12"><p className="faint" style={{ fontSize: 'var(--step--1)' }}>Payroll prorates a mid-period change automatically. Periods already approved cannot change; use a payroll correction for those.</p></div>
    </Shell>
  );
}

/** Upload or replace one scan: KTP, KK, NPWP, a BPJS card or the bank book. */
export function DocumentDialog({ id, kind, has, onClose }: { id: string; kind: PersonDocumentKind; has: boolean; onClose: () => void }) {
  const label = PERSON_DOCUMENTS.find((d) => d.kind === kind)!.label;
  const [file, setFile] = useState<File | null>(null);
  const [tried, setTried] = useState(false);
  const problem = documentProblem(file, tried);
  const m = useSave(id, onClose, () => upload(`/api/people/${id}/documents/${kind}`, file!), has ? `${label} replaced` : `${label} uploaded`);
  const save = () => {
    setTried(true);
    if (!documentProblem(file, true)) m.mutate();
  };
  return (
    <Shell eyebrow="Restricted" title={has ? `Replace the ${label}` : `Upload the ${label}`} submit={has ? 'Replace' : 'Upload'} onClose={onClose} onSave={save} pending={m.isPending} error={m.error}>
      {has ? <div className="col-12"><Callout tone="info">The one it replaces is kept, and the audit trail names it.</Callout></div> : null}
      <DocumentField label={label} onChange={setFile} problem={problem} />
    </Shell>
  );
}

export interface RecordRef { id: string; kind: string; description: string | null; fromDate: string | null; toDate: string | null; hasDocument: boolean }

/** Add a record (legacy employee records: SP letters, signed statements, forms) with its scan, or edit one. */
export function RecordDialog({ id, record, onClose }: { id: string; record: RecordRef | null; onClose: () => void }) {
  const qc = useQueryClient();
  const kinds = useQuery({ queryKey: ['record-kinds'], queryFn: () => get<string[]>('/api/people/records/kinds'), staleTime: 10 * 60_000 });
  const f = useForm({ kind: record?.kind ?? '', description: record?.description ?? '', fromDate: record?.fromDate ?? '', toDate: record?.toDate ?? '' });
  const [file, setFile] = useState<File | null>(null);
  const [saved, setSaved] = useState<string | null>(record?.id ?? null);
  const problem = documentProblem(file);
  const m = useSave(id, onClose, async () => {
    let rid = saved;
    if (!record && !rid) {
      rid = (await post<{ id: string }>(`/api/people/${id}/records`, f.changed())).id;
      setSaved(rid);
      void qc.invalidateQueries({ queryKey: ['person', id] });
    } else if (record && f.dirty) {
      await patch(`/api/people/${id}/records/${record.id}`, f.changed());
    }
    if (file) await upload(`/api/people/${id}/records/${rid}/document`, file);
    void qc.invalidateQueries({ queryKey: ['record-kinds'] });
  }, record ? 'Record saved' : 'Record added');
  const ready = f.values.kind.trim().length >= 2;
  return (
    <Shell eyebrow="Records" title={record ? `Edit “${record.kind}”` : 'Add a record'} submit={!record && saved ? 'Upload' : record ? 'Save' : 'Add'} onClose={onClose} onSave={() => (!ready || problem ? undefined : m.mutate())} pending={m.isPending} error={m.error} width={600}>
      {!record && saved ? <div className="col-12"><Callout tone="info">The record is saved. Only its file is left to upload.</Callout></div> : null}
      <div className="col-12">
        <Field label="Kind" hint="Pick a common one or type your own, e.g. SP 1, PTKP Employee, Surat Pernyataan Tax">
          {(a) => (
            <>
              <input {...a} className="input" list="record-kinds" value={f.values.kind} disabled={!record && !!saved} onChange={(e) => f.set('kind', e.target.value)} />
              <datalist id="record-kinds">{kinds.data?.map((k) => <option key={k} value={k} />)}</datalist>
            </>
          )}
        </Field>
      </div>
      <div className="col-12"><Field label="Notes">{(a) => <textarea {...a} className="textarea" value={f.values.description} disabled={!record && !!saved} onChange={(e) => f.set('description', e.target.value)} />}</Field></div>
      {text('From', f.values.fromDate, (v) => f.set('fromDate', v), 6, { type: 'date', hint: 'Optional' })}
      {text('Until', f.values.toDate, (v) => f.set('toDate', v), 6, { type: 'date', hint: 'Optional, e.g. when an SP lapses' })}
      <DocumentField label={record?.hasDocument ? 'Replace the file (optional)' : 'File (optional)'} onChange={setFile} problem={problem} />
    </Shell>
  );
}

export interface AccountRef { email: string; status: string; locked: boolean }

/** HR changes someone's sign-in e-mail or whether they may sign in (legacy "update login"). Roles stay under Access. */
export function AccountDialog({ id, name, account, onClose }: { id: string; name: string; account: AccountRef; onClose: () => void }) {
  const [email, setEmail] = useState(account.email);
  const [allowed, setAllowed] = useState(account.status !== 'blocked');
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const m = useSave(id, onClose, () => patch(`/api/people/${id}/account`, { email: email.trim(), status: allowed ? 'active' : 'blocked', reason }), 'Sign-in updated');
  const changed = email.trim().toLowerCase() !== account.email.toLowerCase() || allowed !== (account.status !== 'blocked');
  const save = () => {
    if (!changed) return onClose();
    setTried(true);
    if (reason.trim().length >= 5) m.mutate();
  };
  return (
    <Shell eyebrow="Sign-in" title={<>{name}’s sign-in</>} onClose={onClose} onSave={save} pending={m.isPending} error={m.error} width={560}>
      {text('Signs in with', email, setEmail, 12, { inputMode: 'email', hint: 'Their Hearth sign-in for every GX app. Their password stays the same; the old address is told about the change.' })}
      <div className="col-12">
        <Switch checked={allowed} onChange={setAllowed} label="May sign in" showLabel />
        <p className="faint" style={{ fontSize: 'var(--step--2)', marginTop: 6 }}>{allowed ? 'Turning this off signs them out everywhere at once.' : 'They can’t sign in to Hearth or the other GX apps until this is on again.'}</p>
      </div>
      {changed ? (
        <div className="col-12">
          <Field label="Why" hint="Kept in the audit trail, e.g. “new work e-mail after the move to Malang”" error={tried && reason.trim().length < 5 ? 'Say why, in a few words.' : null}>
            {(a) => <input {...a} className="input" value={reason} onChange={(e) => setReason(e.target.value)} />}
          </Field>
        </div>
      ) : null}
    </Shell>
  );
}

export function useRemove(id: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (path: string) => api(`/api/people/${id}/${path}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['person', id] });
      toast({ title: 'Removed' });
    },
    onError: (e) => toast({ title: err(e), tone: 'error' }),
  });
}

/** Lifts the lock after too many wrong passwords. */
export function useUnlock(id: string) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: () => post(`/api/people/${id}/account/unlock`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['person', id] });
      toast({ title: 'Unlocked', body: 'They can try their password again.' });
    },
    onError: (e) => toast({ title: err(e), tone: 'error' }),
  });
}

/** Sends a set-up link; with no e-mail outside GX Mail to send it to, `onHandOver` gets the link for HR to give in person. */
export function useInvite(id: string, onHandOver: (link: string) => void) {
  const toast = useToast();
  return useMutation({
    mutationFn: () => post<{ sentTo: string | null; link?: string }>(`/api/people/${id}/invite`),
    onSuccess: (r) => (r.link ? onHandOver(r.link) : toast({ title: 'Set-up link sent', body: r.sentTo ?? undefined })),
    onError: (e) => toast({ title: err(e), tone: 'error' }),
  });
}
